import * as THREE from 'three';

type Spark = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
};

type BurstLight = {
  light: THREE.PointLight;
  life: number;
  maxLife: number;
  peakIntensity: number;
};

const LIFE = 0.4;

/** Golden spark burst + brief flash when a chest opens. */
export class FpsChestBurst {
  private readonly sparks: Spark[] = [];
  private readonly lights: BurstLight[] = [];
  private readonly geo = new THREE.BoxGeometry(0.045, 0.045, 0.045);
  private readonly normalColors = [0xffee88, 0xffd54a, 0xffffff, 0xffcc44];
  private readonly rareColors = [0xffffff, 0xffeeaa, 0xffd700, 0xddaaff, 0xffccff];

  constructor(private readonly scene: THREE.Scene) {}

  spawn(x: number, y: number, z: number, tier: 'normal' | 'rare'): void {
    const colors = tier === 'rare' ? this.rareColors : this.normalColors;
    const count = 12 + Math.floor(Math.random() * 5);

    const light = new THREE.PointLight(
      tier === 'rare' ? 0xffeecc : 0xffdd88,
      tier === 'rare' ? 9 : 6,
      tier === 'rare' ? 12 : 9,
    );
    light.position.set(x, y + 0.15, z);
    this.scene.add(light);
    this.lights.push({
      light,
      life: 0,
      maxLife: LIFE,
      peakIntensity: light.intensity,
    });

    for (let i = 0; i < count; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: colors[i % colors.length]!,
        transparent: true,
        opacity: 0.98,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const mesh = new THREE.Mesh(this.geo, mat);
      mesh.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI,
      );
      mesh.position.set(
        x + (Math.random() - 0.5) * 0.18,
        y + 0.08 + Math.random() * 0.12,
        z + (Math.random() - 0.5) * 0.18,
      );
      mesh.scale.setScalar(0.55 + Math.random() * 0.65);
      this.scene.add(mesh);

      const spread = 0.45 + Math.random() * 0.75;
      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * spread,
        1.4 + Math.random() * 2.2,
        (Math.random() - 0.5) * spread,
      );
      if (tier === 'rare') vel.multiplyScalar(1.15);

      this.sparks.push({ mesh, vel, life: 0, maxLife: LIFE * (0.82 + Math.random() * 0.28) });
    }
  }

  update(dt: number): void {
    for (let i = this.lights.length - 1; i >= 0; i--) {
      const l = this.lights[i]!;
      l.life += dt;
      const t = l.life / l.maxLife;
      l.light.intensity = l.peakIntensity * Math.max(0, 1 - t * t);
      if (l.life >= l.maxLife) {
        this.scene.remove(l.light);
        l.light.dispose();
        this.lights.splice(i, 1);
      }
    }

    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i]!;
      s.life += dt;
      s.mesh.position.addScaledVector(s.vel, dt);
      s.vel.y -= dt * 3.6;
      s.vel.multiplyScalar(1 - dt * 2.2);
      s.mesh.rotation.x += dt * 8.5;
      s.mesh.rotation.y += dt * 6.2;
      const t = s.life / s.maxLife;
      const mat = s.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, 0.98 * (1 - t * t));
      s.mesh.scale.setScalar((0.55 + t * 0.2) * (1 - t * 0.4));
      if (s.life >= s.maxLife) {
        this.scene.remove(s.mesh);
        mat.dispose();
        this.sparks.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const s of this.sparks) {
      this.scene.remove(s.mesh);
      (s.mesh.material as THREE.Material).dispose();
    }
    this.sparks.length = 0;
    for (const l of this.lights) {
      this.scene.remove(l.light);
      l.light.dispose();
    }
    this.lights.length = 0;
    this.geo.dispose();
  }
}
